import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { OriginBadge } from "../components/Badges";
import { toast } from "sonner";
import { Plus, Sparkles, ArrowLeft } from "lucide-react";

const ROLE_TO_ORIGIN = { FOUNDER: "FOUNDER", AI: "AI", THIRD_PARTY: "THIRD_PARTY", UNKNOWN: "UNKNOWN" };

export default function Conversations() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => api.get("/conversations").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const view = (id) => { setActive(id); api.get(`/conversations/${id}`).then((r) => setDetail(r.data)); };

  const extract = async () => {
    const srcId = detail?.conversation?.raw_source_id;
    // extraction endpoint keyed by source id — resolve via sources list
    try {
      const sources = (await api.get("/sources")).data;
      const src = sources.find((s) => s.id === srcId);
      if (!src) { toast.error("Source not found"); return; }
      const { data } = await api.post(`/sources/${src.id}/extract-claims`);
      toast.success(`${data.created} candidate claim(s) extracted for review.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Extraction failed");
    }
  };

  if (active && detail) {
    return (
      <div>
        <PageHeader title={detail.conversation.title} subtitle={`${detail.conversation.platform} · stored unchanged with speaker attribution preserved`}
          right={<Button onClick={extract} className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2" data-testid="extract-conversation-claims"><Sparkles size={15} /> Extract Claims</Button>} />
        <div className="p-10">
          <button onClick={() => { setActive(null); setDetail(null); }} className="flex items-center gap-2 text-sm text-slate-500 mb-6 hover:text-slate-900" data-testid="back-to-conversations"><ArrowLeft size={15} /> All conversations</button>
          <div className="max-w-3xl space-y-4" data-testid="conversation-messages">
            {detail.messages.map((m) => (
              <div key={m.id} className="bg-white border border-slate-200 p-5" data-testid={`message-${m.sequence_number}`}>
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-mono text-[11px] text-slate-400">#{m.sequence_number}</span>
                  <OriginBadge origin={ROLE_TO_ORIGIN[m.role] || "UNKNOWN"} />
                  {m.model && <span className="font-mono text-[10px] text-slate-400 uppercase">{m.model}</span>}
                </div>
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{m.content}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Conversations" subtitle="Historical AI conversations preserved verbatim, with speaker/role attribution and chronological order."
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2" data-testid="import-conversation-btn"><Plus size={16} /> Import Conversation</Button></DialogTrigger>
            <ImportForm onDone={() => { setOpen(false); load(); }} />
          </Dialog>
        } />
      <div className="p-10">
        <div className="border border-slate-200 bg-white divide-y divide-slate-100" data-testid="conversations-list">
          {list.length === 0 && <div className="p-8 text-slate-400 text-sm">No conversations imported yet.</div>}
          {list.map((c) => (
            <button key={c.id} onClick={() => view(c.id)} className="w-full text-left px-6 py-5 hover:bg-slate-50 transition-colors flex items-center gap-4" data-testid={`conversation-${c.id}`}>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-800">{c.title}</h3>
                <div className="text-xs text-slate-500 font-mono mt-1">{c.platform}{c.project ? ` · ${c.project}` : ""}</div>
              </div>
              <span className="font-mono text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-sm">{c.message_count} messages</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ImportForm({ onDone }) {
  const [title, setTitle] = useState("");
  const [platform, setPlatform] = useState("ChatGPT");
  const [project, setProject] = useState("");
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim() || !raw.trim()) { toast.error("Title and transcript are required."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/conversations", { title, platform, project, raw_text: raw });
      toast.success(`Imported ${data.messages} messages, stored unchanged.`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <DialogContent className="max-w-xl rounded-sm bg-white" data-testid="conversation-form">
      <DialogHeader><DialogTitle className="font-serif text-2xl">Import Conversation</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div>
          <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5 rounded-sm border-slate-300" data-testid="conv-title" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Platform</Label>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger className="mt-1.5 rounded-sm border-slate-300" data-testid="conv-platform"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{["ChatGPT", "Claude", "Gemini", "Other"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Project (optional)</Label>
            <Input value={project} onChange={(e) => setProject(e.target.value)} className="mt-1.5 rounded-sm border-slate-300" data-testid="conv-project" />
          </div>
        </div>
        <div>
          <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Transcript</Label>
          <p className="text-xs text-slate-400 mb-1.5">Prefix each turn with a speaker, e.g. <code className="font-mono">Founder:</code> or <code className="font-mono">ChatGPT:</code> / <code className="font-mono">AI:</code></p>
          <Textarea value={raw} onChange={(e) => setRaw(e.target.value)} className="rounded-sm border-slate-300 min-h-[180px] font-mono text-sm" data-testid="conv-transcript"
            placeholder={"Founder: I think PRAMAANA is trust infrastructure, not a booking app.\nChatGPT: You could add astrology upsells to boost revenue.\nFounder: No — fear-based monetisation is against our philosophy."} />
        </div>
        <Button onClick={submit} disabled={busy} className="w-full rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="conv-submit">{busy ? "Importing…" : "Import & Preserve"}</Button>
      </div>
    </DialogContent>
  );
}
